import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { Trash2, Edit, CalendarClock } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export const CreatePlanDialog = ({ open, onOpenChange, planFormData, setPlanFormData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="create-plan-dialog">
      <DialogHeader><DialogTitle>Create Study Plan Template</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label htmlFor="plan-name">Plan Name</Label>
          <Input id="plan-name" value={planFormData.name} onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })} placeholder="e.g., Beginner Python Course" data-testid="plan-name-input" />
        </div>
        <div>
          <Label htmlFor="plan-description">Description</Label>
          <Textarea id="plan-description" value={planFormData.description} onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })} placeholder="Brief description of this study plan" data-testid="plan-description-input" />
        </div>
        <div>
          <Label htmlFor="plan-path-plan">Study Path Plan</Label>
          <Textarea id="plan-path-plan" value={planFormData.path_plan || ''} onChange={(e) => setPlanFormData({ ...planFormData, path_plan: e.target.value })} placeholder="Outline the learning path or curriculum structure" data-testid="plan-path-plan-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-create-plan">Create Study Plan</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const EditPlanDialog = ({ open, onOpenChange, planFormData, setPlanFormData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="edit-plan-dialog">
      <DialogHeader><DialogTitle>Edit Study Plan</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label htmlFor="edit-plan-name">Plan Name</Label>
          <Input id="edit-plan-name" value={planFormData.name} onChange={(e) => setPlanFormData({ ...planFormData, name: e.target.value })} data-testid="edit-plan-name-input" />
        </div>
        <div>
          <Label htmlFor="edit-plan-description">Description</Label>
          <Textarea id="edit-plan-description" value={planFormData.description} onChange={(e) => setPlanFormData({ ...planFormData, description: e.target.value })} data-testid="edit-plan-description-input" />
        </div>
        <div>
          <Label htmlFor="edit-plan-path-plan">Study Path Plan</Label>
          <Textarea id="edit-plan-path-plan" value={planFormData.path_plan || ''} onChange={(e) => setPlanFormData({ ...planFormData, path_plan: e.target.value })} data-testid="edit-plan-path-plan-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-edit-plan">Update Study Plan</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ClonePlanDialog = ({ open, onOpenChange, planToClone, cloneFormData, setCloneFormData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="clone-plan-dialog">
      <DialogHeader><DialogTitle>Clone Study Plan Template</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
          <p className="text-sm text-blue-800">Cloning: <strong>{planToClone?.name}</strong></p>
          <p className="text-xs text-blue-600 mt-1">All tasks will be copied to the new study plan</p>
        </div>
        <div>
          <Label htmlFor="clone-name">New Plan Name</Label>
          <Input id="clone-name" value={cloneFormData.new_name} onChange={(e) => setCloneFormData({ ...cloneFormData, new_name: e.target.value })} placeholder="e.g., Advanced Python Course" data-testid="clone-name-input" />
        </div>
        <div>
          <Label htmlFor="clone-description">New Description (Optional)</Label>
          <Textarea id="clone-description" value={cloneFormData.new_description} onChange={(e) => setCloneFormData({ ...cloneFormData, new_description: e.target.value })} placeholder="Update the description for the cloned plan" data-testid="clone-description-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-clone-plan">Clone Study Plan</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const TaskFormDialog = ({ open, onOpenChange, title, taskFormData, setTaskFormData, onSubmit, submitLabel, isEdit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid={isEdit ? "edit-task-dialog" : "create-task-dialog"}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label htmlFor={isEdit ? "edit-activity" : "activity"}>Activity</Label>
          <Input id={isEdit ? "edit-activity" : "activity"} value={taskFormData.activity} onChange={(e) => setTaskFormData({ ...taskFormData, activity: e.target.value })} placeholder="e.g., Complete Chapter 1" data-testid={isEdit ? "edit-activity-input" : "create-activity-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-content" : "content"}>Content</Label>
          <Textarea id={isEdit ? "edit-content" : "content"} value={taskFormData.content} onChange={(e) => setTaskFormData({ ...taskFormData, content: e.target.value })} placeholder="Detailed content description" data-testid={isEdit ? "edit-content-input" : "create-content-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-goals" : "goals"}>Goals</Label>
          <Textarea id={isEdit ? "edit-goals" : "goals"} value={taskFormData.goals} onChange={(e) => setTaskFormData({ ...taskFormData, goals: e.target.value })} placeholder="Learning goals" data-testid={isEdit ? "edit-goals-input" : "create-goals-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-days-offset" : "days_offset"}>Days Offset</Label>
          <Input id={isEdit ? "edit-days-offset" : "days_offset"} type="number" value={taskFormData.days_offset} onChange={(e) => setTaskFormData({ ...taskFormData, days_offset: parseInt(e.target.value) })} data-testid={isEdit ? "edit-days-offset-input" : "create-days-offset-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-status" : "status"}>Status</Label>
          <Select value={taskFormData.status} onValueChange={(val) => setTaskFormData({ ...taskFormData, status: val })}>
            <SelectTrigger data-testid={isEdit ? "edit-status-select" : "create-status-select"}><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="open">Open</SelectItem>
              <SelectItem value="complete">Complete</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-category" : "category"}>Category (optional)</Label>
          <Input id={isEdit ? "edit-category" : "category"} value={taskFormData.category || ''} onChange={(e) => setTaskFormData({ ...taskFormData, category: e.target.value || null })} placeholder="e.g., Domain A – Strategic Planning" data-testid={isEdit ? "edit-category-input" : "create-category-input"} />
        </div>
        {isEdit && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-xs text-blue-800">Changes will propagate to all users assigned to this study plan (except hours_log).</p>
          </div>
        )}
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid={isEdit ? "submit-edit-task" : "submit-create-task"}>{submitLabel}</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const AssignPlanDialog = ({ open, onOpenChange, users, studyPlans, selectedUserId, setSelectedUserId, selectedPlanForAssign, setSelectedPlanForAssign, onSubmit }) => {
  const [categories, setCategories] = useState([]);
  const [selectedCategories, setSelectedCategories] = useState([]);
  const [loadingCats, setLoadingCats] = useState(false);

  useEffect(() => {
    if (!selectedPlanForAssign) {
      setCategories([]);
      setSelectedCategories([]);
      return;
    }
    const fetchCategories = async () => {
      setLoadingCats(true);
      try {
        const { data } = await axios.get(`${BACKEND_URL}/api/admin/study-plan-templates/${selectedPlanForAssign}/categories`, { withCredentials: true });
        setCategories(data || []);
        setSelectedCategories(data || []); // default all selected
      } catch {
        setCategories([]);
      }
      setLoadingCats(false);
    };
    fetchCategories();
  }, [selectedPlanForAssign]);

  const toggleCategory = (cat) => {
    setSelectedCategories(prev =>
      prev.includes(cat) ? prev.filter(c => c !== cat) : [...prev, cat]
    );
  };

  const selectAll = () => setSelectedCategories([...categories]);
  const deselectAll = () => setSelectedCategories([]);

  const handleSubmit = () => {
    // Pass categories to parent if plan has them
    if (categories.length > 0) {
      onSubmit(selectedCategories.length > 0 ? selectedCategories : null);
    } else {
      onSubmit(null);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="assign-plan-dialog">
        <DialogHeader><DialogTitle>Assign Study Plan to User</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-4">
          <div>
            <Label htmlFor="select-user">Select User</Label>
            <Select value={selectedUserId} onValueChange={setSelectedUserId}>
              <SelectTrigger data-testid="select-user"><SelectValue placeholder="Choose a user" /></SelectTrigger>
              <SelectContent>{users.map((u) => (<SelectItem key={u.id} value={u.id}>{u.name} ({u.email})</SelectItem>))}</SelectContent>
            </Select>
          </div>
          <div>
            <Label htmlFor="select-plan">Select Study Plan</Label>
            <Select value={selectedPlanForAssign} onValueChange={setSelectedPlanForAssign}>
              <SelectTrigger data-testid="select-plan"><SelectValue placeholder="Choose a study plan" /></SelectTrigger>
              <SelectContent>{studyPlans.map((plan) => (<SelectItem key={plan.id} value={plan.id}>{plan.name}</SelectItem>))}</SelectContent>
            </Select>
          </div>

          {/* Category selection (only shows if plan has categories) */}
          {categories.length > 0 && (
            <div className="border border-slate-200 rounded-lg p-3" data-testid="category-selection">
              <div className="flex items-center justify-between mb-2">
                <Label className="text-sm font-semibold text-slate-700">Select Domain Categories</Label>
                <div className="flex gap-2">
                  <button onClick={selectAll} className="text-xs text-blue-600 hover:text-blue-700 font-medium">All</button>
                  <span className="text-xs text-slate-300">|</span>
                  <button onClick={deselectAll} className="text-xs text-slate-500 hover:text-slate-700 font-medium">None</button>
                </div>
              </div>
              <p className="text-xs text-slate-500 mb-2">Non-domain tasks (prep exams, quick fires, etc.) are always included.</p>
              <div className="space-y-1.5 max-h-48 overflow-y-auto">
                {categories.map(cat => (
                  <label key={cat} className="flex items-center gap-2 cursor-pointer hover:bg-slate-50 px-2 py-1 rounded" data-testid={`category-${cat}`}>
                    <input
                      type="checkbox"
                      checked={selectedCategories.includes(cat)}
                      onChange={() => toggleCategory(cat)}
                      className="rounded border-slate-300 text-blue-600 focus:ring-blue-500"
                    />
                    <span className="text-sm text-slate-700">{cat}</span>
                  </label>
                ))}
              </div>
              <p className="text-xs text-slate-500 mt-2 font-medium">{selectedCategories.length} of {categories.length} domains selected</p>
            </div>
          )}

          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-xs text-blue-800">This will add the study plan to the user. Users can have multiple plans assigned.</p>
          </div>
          <Button onClick={handleSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-assign-plan">Assign Study Plan</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const ManagePlansDialog = ({ open, onOpenChange, user, onRemovePlan, onEditStartDate, onEditExpiration }) => {
  const plans = user?.assigned_plans || [];
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-2xl max-h-[80vh] overflow-y-auto" data-testid="manage-plans-dialog">
        <DialogHeader><DialogTitle>Manage Plans for {user?.name}</DialogTitle></DialogHeader>
        <div className="mt-4">
          {plans.length === 0 ? (
            <div className="text-center py-8">
              <p className="text-slate-500">No plans assigned yet. Use "Assign Plan" to add one.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {plans.map((plan) => {
                const isExpired = plan.expiration_date && new Date(plan.expiration_date) < new Date();
                return (
                  <div key={plan.study_plan_template_id} className="border border-slate-200 rounded-lg p-4" data-testid={`manage-plan-${plan.study_plan_template_id}`}>
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900">{plan.study_plan_name}</h4>
                      <Button size="sm" variant="outline" className="text-red-600 hover:text-red-700"
                        onClick={() => onRemovePlan(user.id, plan.study_plan_template_id, plan.study_plan_name)}
                        data-testid={`remove-plan-${plan.study_plan_template_id}`}>
                        <Trash2 className="w-3 h-3 mr-1" />Remove
                      </Button>
                    </div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                        <div>
                          <p className="text-xs text-slate-500">Start Date</p>
                          <p className="text-sm font-medium">{plan.start_date || 'Not set'}</p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => onEditStartDate(user, plan)}
                          data-testid={`edit-start-${plan.study_plan_template_id}`}>
                          <Edit className="w-3 h-3" />
                        </Button>
                      </div>
                      <div className="flex items-center justify-between bg-slate-50 rounded-lg p-2">
                        <div>
                          <p className="text-xs text-slate-500">Expiration</p>
                          <p className={`text-sm font-medium ${isExpired ? 'text-red-600' : ''}`}>
                            {plan.expiration_date ? (isExpired ? `Expired (${plan.expiration_date})` : plan.expiration_date) : 'No expiry'}
                          </p>
                        </div>
                        <Button size="sm" variant="ghost" onClick={() => onEditExpiration(user, plan)}
                          data-testid={`edit-expiry-${plan.study_plan_template_id}`}>
                          <CalendarClock className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const StartDateDialog = ({ open, onOpenChange, user, planName, newStartDate, setNewStartDate, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="edit-start-date-dialog">
      <DialogHeader><DialogTitle>Set Start Date</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
          <p className="text-sm text-blue-800">User: <strong>{user?.name}</strong></p>
          {planName && <p className="text-xs text-blue-600 mt-1">Plan: {planName}</p>}
          <p className="text-xs text-blue-600 mt-1">Task due dates for this plan will be recalculated</p>
        </div>
        <div>
          <Label htmlFor="admin-start-date">Start Date</Label>
          <Input id="admin-start-date" type="date" value={newStartDate} onChange={(e) => setNewStartDate(e.target.value)} data-testid="admin-start-date-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-start-date">Update Start Date</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ViewAsUserDialog = ({ open, onOpenChange, selectedUser, userViewTasks, viewAsFilter, setViewAsFilter, filteredViewAsTasks, onToggleStatus, studyPlans }) => {
  const planNames = (selectedUser?.assigned_plans || []).map(p => p.study_plan_name).filter(Boolean);
  const plansLabel = planNames.length > 0 ? planNames.join(', ') : 'Not assigned';
  // build lookup from plan id to name
  const planLookup = {};
  (studyPlans || []).forEach(p => { planLookup[p.id] = p.name; });
  (selectedUser?.assigned_plans || []).forEach(p => { planLookup[p.study_plan_template_id] = p.study_plan_name; });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[95vw] sm:max-w-5xl max-h-[80vh] overflow-y-auto" data-testid="view-as-user-dialog">
        <DialogHeader><DialogTitle>Viewing as: {selectedUser?.name}</DialogTitle></DialogHeader>
        <div className="mt-4">
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg mb-4">
            <p className="text-sm text-blue-800"><strong>Admin Preview Mode</strong> - This is what {selectedUser?.name} sees in their dashboard</p>
            <p className="text-xs text-blue-600 mt-1">Email: {selectedUser?.email} | Plans: {plansLabel}</p>
          </div>
          <div className="flex justify-between items-center mb-4">
            <Select value={viewAsFilter} onValueChange={setViewAsFilter}>
              <SelectTrigger className="w-40" data-testid="view-as-filter"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Tasks</SelectItem>
                <SelectItem value="open">Open Only</SelectItem>
                <SelectItem value="complete">Complete Only</SelectItem>
              </SelectContent>
            </Select>
            <div className="text-sm text-slate-600">Showing {filteredViewAsTasks.length} of {userViewTasks.length} tasks</div>
          </div>
          {filteredViewAsTasks.length === 0 ? (
            <div className="text-center py-12"><p className="text-slate-500">No tasks found for this filter.</p></div>
          ) : (
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Activity</TableHead><TableHead>Content</TableHead><TableHead>Goals</TableHead>
                    <TableHead>Due Date</TableHead><TableHead>Hours Log</TableHead><TableHead>Status</TableHead>
                    <TableHead>Plan</TableHead><TableHead>Type</TableHead><TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredViewAsTasks.map((task) => (
                    <TableRow key={task.id}>
                      <TableCell className="font-medium">{task.activity}</TableCell>
                      <TableCell className="max-w-xs truncate">{task.content}</TableCell>
                      <TableCell className="max-w-xs truncate">{task.goals}</TableCell>
                      <TableCell>{task.due_date || 'Not set'}</TableCell>
                      <TableCell>{task.hours_log} hrs</TableCell>
                      <TableCell>
                        <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${task.status === 'open' ? 'bg-blue-100 text-blue-800 border border-blue-200' : 'bg-slate-100 text-slate-800 border border-slate-200'}`}>{task.status}</span>
                      </TableCell>
                      <TableCell>
                        <span className="text-xs text-slate-500">{planLookup[task.study_plan_template_id] || '—'}</span>
                      </TableCell>
                      <TableCell>
                        {task.is_custom ? <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-100 text-purple-700 border border-purple-200">Custom</span>
                          : <span className="px-2 py-0.5 rounded text-xs font-semibold bg-green-100 text-green-700 border border-green-200">Assigned</span>}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="outline" className={task.status === 'open' ? 'text-green-600 hover:text-green-700' : 'text-blue-600 hover:text-blue-700'}
                          onClick={() => onToggleStatus(task)} data-testid={`toggle-status-${task.id}`}>
                          {task.status === 'open' ? 'Complete' : 'Reopen'}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const MilestoneDialog = ({ open, onOpenChange, title, milestoneFormData, setMilestoneFormData, onSubmit, submitLabel, isEdit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid={isEdit ? "edit-milestone-dialog" : "create-milestone-dialog"}>
      <DialogHeader><DialogTitle>{title}</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label htmlFor={isEdit ? "edit-milestone-title" : "milestone-title"}>Title</Label>
          <Input id={isEdit ? "edit-milestone-title" : "milestone-title"} value={milestoneFormData.title} onChange={(e) => setMilestoneFormData({ ...milestoneFormData, title: e.target.value })} placeholder="e.g., Complete Module 1" data-testid={isEdit ? "edit-milestone-title-input" : "milestone-title-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-milestone-description" : "milestone-description"}>Description (optional)</Label>
          <Textarea id={isEdit ? "edit-milestone-description" : "milestone-description"} value={milestoneFormData.description} onChange={(e) => setMilestoneFormData({ ...milestoneFormData, description: e.target.value })} placeholder="Brief description of this milestone" data-testid={isEdit ? "edit-milestone-description-input" : "milestone-description-input"} />
        </div>
        <div>
          <Label htmlFor={isEdit ? "edit-milestone-order" : "milestone-order"}>Order</Label>
          <Input id={isEdit ? "edit-milestone-order" : "milestone-order"} type="number" value={milestoneFormData.order} onChange={(e) => setMilestoneFormData({ ...milestoneFormData, order: parseInt(e.target.value) || 0 })} data-testid={isEdit ? "edit-milestone-order-input" : "milestone-order-input"} />
        </div>
        {isEdit && (
          <div className="bg-blue-50 border border-blue-200 p-3 rounded-lg">
            <p className="text-xs text-blue-800">Changes will be visible to all users assigned to this study plan.</p>
          </div>
        )}
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid={isEdit ? "submit-edit-milestone" : "submit-create-milestone"}>{submitLabel}</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const ExpirationDialog = ({ open, onOpenChange, user, planName, expirationDate, setExpirationDate, onSubmit, onClear }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="expiration-dialog">
      <DialogHeader><DialogTitle>Set Expiration Date</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-sm text-slate-600">User: <strong>{user?.name}</strong> ({user?.email})</p>
        {planName && <p className="text-sm text-blue-600">Plan: <strong>{planName}</strong></p>}
        <div>
          <Label htmlFor="expiration-date">Expiration Date</Label>
          <Input id="expiration-date" type="date" value={expirationDate} onChange={(e) => setExpirationDate(e.target.value)} data-testid="expiration-date-input" />
        </div>
        <div className="flex gap-2">
          <Button onClick={onSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-expiration">Save</Button>
          <Button variant="outline" onClick={onClear} className="text-red-600 hover:text-red-700" data-testid="clear-expiration">Remove Expiry</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const BulkExpirationDialog = ({ open, onOpenChange, studyPlans, bulkExpirationPlanId, setBulkExpirationPlanId, bulkExpirationDate, setBulkExpirationDate, onSubmit, onClear }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="bulk-expiration-dialog">
      <DialogHeader><DialogTitle>Bulk Set Expiration</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-sm text-slate-600">Set an expiration date for all users assigned to a study plan.</p>
        <div>
          <Label htmlFor="bulk-plan">Study Plan</Label>
          <Select value={bulkExpirationPlanId} onValueChange={setBulkExpirationPlanId}>
            <SelectTrigger data-testid="bulk-plan-select"><SelectValue placeholder="Select a plan" /></SelectTrigger>
            <SelectContent>{studyPlans.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}</SelectContent>
          </Select>
        </div>
        <div>
          <Label htmlFor="bulk-expiration-date">Expiration Date</Label>
          <Input id="bulk-expiration-date" type="date" value={bulkExpirationDate} onChange={(e) => setBulkExpirationDate(e.target.value)} data-testid="bulk-expiration-date-input" />
        </div>
        <div className="flex gap-2">
          <Button onClick={onSubmit} className="flex-1 bg-blue-600 hover:bg-blue-700" data-testid="submit-bulk-expiration">Apply to All Users</Button>
          <Button variant="outline" onClick={onClear} className="text-red-600 hover:text-red-700" data-testid="clear-bulk-expiration">Remove All</Button>
        </div>
      </div>
    </DialogContent>
  </Dialog>
);

export const ResetPasswordDialog = ({ open, onOpenChange, user, password, setPassword, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="admin-reset-password-dialog">
      <DialogHeader><DialogTitle>Reset User Password</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <p className="text-sm text-slate-600">Reset password for: <strong>{user?.name}</strong> ({user?.email})</p>
        <div>
          <Label htmlFor="admin-new-password">New Password</Label>
          <Input id="admin-new-password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="Min 6 characters" data-testid="admin-new-password-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-admin-reset-password">Reset Password</Button>
      </div>
    </DialogContent>
  </Dialog>
);

export const CreateUserDialog = ({ open, onOpenChange, formData, setFormData, onSubmit }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent data-testid="create-user-dialog">
      <DialogHeader><DialogTitle>Add New User</DialogTitle></DialogHeader>
      <div className="space-y-4 mt-4">
        <div>
          <Label htmlFor="new-user-name">Full Name</Label>
          <Input id="new-user-name" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="e.g., John Smith" data-testid="new-user-name-input" />
        </div>
        <div>
          <Label htmlFor="new-user-email">Email</Label>
          <Input id="new-user-email" type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="e.g., john@example.com" data-testid="new-user-email-input" />
        </div>
        <div>
          <Label htmlFor="new-user-password">Password</Label>
          <Input id="new-user-password" type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Min 6 characters" data-testid="new-user-password-input" />
        </div>
        <Button onClick={onSubmit} className="w-full bg-blue-600 hover:bg-blue-700" data-testid="submit-create-user">Add User</Button>
      </div>
    </DialogContent>
  </Dialog>
);
